import React, { useState } from 'react'
import Icon from '@chegg-wt/core/components/Icon'
import Select from '@chegg-wt/core/components/Select'
import { GATED_EXPORT_MODAL } from '@chegg-wt/core/state/modals/constants'
import { CITATION_LIST_NAME_LOWER_SINGLE } from '@chegg-wt/core/constants/citations'
import { Option } from '@chegg-wt/core/types/core'
import * as BaseStyled from '../styled'
import * as Styled from './styled'
import {
  EXPORT_MODAL_SELECT_DROPDOWN_EXPORT,
  EXPORT_MODAL_SELECT_DROPDOWN_MOVE,
  EXPORT_MODAL_CANCEL_BUTTON,
  EXPORT_MODAL_SUBMIT_BUTTON,
  EXPORT_MODAL_TAB,
} from './testIds'
import { ExportTabId, ExportTabProps, DropdownOption } from './types'

const GuestTabType = {
  EXPORT: 'export',
}

export const exportOptions = (exportTabIds: ExportTabId[]) => {
  const options = {
    docx: { name: 'Microsoft Word', id: 'docx', gated: false },
    gdocs: { name: 'Google Docs', id: 'gdocs', gated: false },
  }
  return exportTabIds.map(({ id, ...overrides }) => ({ ...options[id], ...overrides }))
}

export const ExportTab: React.FC<ExportTabProps> = ({
  exportTabIds = [{ id: 'docx' }, { id: 'gdocs' }],
  projects,
  citations,
  includeMove,
  isPro,
  exportCitations,
  moveCitations,
  handleClose,
  handleComplete,
  openModal,
}) => {
  const TabType = includeMove
    ? Object.assign(GuestTabType, { MOVE: 'move' })
    : GuestTabType

  // UI state
  const [exportDropdown, setExportDropdown] = useState<DropdownOption>()
  const [moveDropdown, setMoveDropdown] = useState<Option>()
  const [tabType, setTabType] = useState(TabType.EXPORT)

  const ExportTrigger = ({ openDropdown }) => (
    <BaseStyled.DropdownTrigger
      data-test={EXPORT_MODAL_SELECT_DROPDOWN_EXPORT}
      onClick={openDropdown}
    >
      {exportDropdown ? exportDropdown.name : 'Select Here'}{' '}
      <Icon name="dropdownArrows" />
    </BaseStyled.DropdownTrigger>
  )

  const MoveTrigger = ({ openDropdown }) => (
    <BaseStyled.DropdownTrigger
      data-test={EXPORT_MODAL_SELECT_DROPDOWN_MOVE}
      onClick={openDropdown}
    >
      {moveDropdown ? moveDropdown.name : `Choose a ${CITATION_LIST_NAME_LOWER_SINGLE}`}{' '}
      <Icon name="dropdownArrows" />
    </BaseStyled.DropdownTrigger>
  )

  const handleSubmit = () => {
    if (tabType === TabType.EXPORT && exportDropdown.gated && !isPro) {
      handleClose() // TODO: keeping this here to keep parody with existing logic
      openModal({
        name: GATED_EXPORT_MODAL,
        data: {
          exportName: exportDropdown.name,
          handleComplete,
        },
      })
    } else if (tabType === TabType.EXPORT) {
      exportCitations(exportDropdown.id?.toString(), citations)
    } else {
      moveCitations(moveDropdown.id?.toString(), citations)
    }
  }

  return (
    <Styled.Body>
      <Styled.Tabs>
        {Object.values(TabType).map((tab) => (
          <Styled.Tab
            data-test={EXPORT_MODAL_TAB(tab)}
            key={tab}
            onClick={() => {
              setTabType(tab)
              setExportDropdown(null)
              setMoveDropdown(null)
            }}
            selected={tabType === tab}
          >
            {tab}
          </Styled.Tab>
        ))}
      </Styled.Tabs>
      <Styled.DropdownText>
        Where would you like to {tabType} your citations?
      </Styled.DropdownText>
      {tabType === TabType.EXPORT ? (
        <Select
          onSelect={(id) => {
            const option = exportOptions(exportTabIds).find((item) => item.id === id)
            setExportDropdown(option)
          }}
          options={exportOptions(exportTabIds)}
          trigger={ExportTrigger}
          type={tabType}
        />
      ) : (
        <Select
          onSelect={(id, name) => setMoveDropdown({ id, name })}
          options={projects}
          trigger={MoveTrigger}
          type={tabType}
        />
      )}
      <Styled.ButtonContainer>
        <Styled.CancelButton data-test={EXPORT_MODAL_CANCEL_BUTTON} onClick={handleClose}>
          Cancel
        </Styled.CancelButton>
        <Styled.SubmitButton
          disabled={!(exportDropdown || moveDropdown)}
          data-test={EXPORT_MODAL_SUBMIT_BUTTON}
          onClick={handleSubmit}
        >
          {tabType}
        </Styled.SubmitButton>
      </Styled.ButtonContainer>
    </Styled.Body>
  )
}
